import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminSettingsPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmNext, setConfirmNext] = useState("");
  const [busy, setBusy] = useState(false);
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirmNext) {
      toast.error("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated.");
      setCurrent(""); setNext(""); setConfirmNext("");
      try { await refreshUser(); } catch { /* non-blocking */ }
      if (forced) navigate("/control-panel-x9", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Change failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-xl px-6 py-12 md:px-10" data-testid="admin-settings-page">
      <div className="mb-rise">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
          <Settings className="h-3 w-3" />
          Settings
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Change password
        </h1>
        {forced && (
          <div
            className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            data-testid="must-change-notice"
          >
            You must change your initial password to continue.
          </div>
        )}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-mb-card" data-testid="change-password-form">
        <div>
          <Label htmlFor="cp-current">Current password</Label>
          <Input id="cp-current" type="password" required autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid="cp-current" />
        </div>
        <div>
          <Label htmlFor="cp-next">New password</Label>
          <Input id="cp-next" type="password" required minLength={8} autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="cp-next" />
        </div>
        <div>
          <Label htmlFor="cp-confirm">Confirm new password</Label>
          <Input id="cp-confirm" type="password" required minLength={8} autoComplete="new-password"
            value={confirmNext} onChange={(e) => setConfirmNext(e.target.value)}
            data-testid="cp-confirm" />
        </div>
        <Button type="submit" disabled={busy} data-testid="cp-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Update password
        </Button>
      </form>
    </div>
  );
}
